import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { openSecureNewTab } from '../utils';
import { Environment, EnvironmentService } from '@galaxy/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  BOOOKING_URL_DEMO,
  BOOOKING_URL_PROD,
  MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$,
  MS_CONTEXT,
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
} from '../constants';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AttachmentInterface,
  Attendee,
  Calendar,
  Meeting,
  MeetingFormAnswer,
  MeetingType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import {
  GroupMeetingType,
  isGroupMeetingType,
  MeetingSchedulerStoreService,
} from '../data-providers/meeting-scheduler-store.service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { AttendeeListComponent } from '../shared/components/attendee-list/attendee-list.component';
import { MatDialog } from '@angular/material/dialog';

export interface CalenderViewDataSource {
  meetingId: string;
  title: string;
  joinMeeting: string;
  date: { start: Date; end: Date };
  guest: string;
  contact: { email: string; phoneNumber: string };
  attendeeEmails: string;
  comments: string;
  location: string;
  created: Date;
  formAnswers: MeetingFormAnswer[];
  attachments: AttachmentInterface[];
  calendarId: string;
  meetingType: TeamEventMeetingType;
  hostNames: string[];
  hostUserIds?: string[];
  topic?: string;
  isSessionEventType: boolean;
  eventTypeId?: string;
  // Session-specific properties
  sessionTitle?: string;
  sessionRegistrationUrl?: string;
  registeredAttendeeCount: string;
  maxAttendeeCount?: string;
  registrationCutoff?: string;
  attendees?: Attendee[];
}

interface TimeSpan {
  start: Date;
  end: Date;
}

interface LoadMeetingFilters {
  meetingTypeIds: string[];
  timeSpan: TimeSpan;
}

@Component({
  selector: 'meeting-scheduler-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalenderViewComponent implements OnInit, OnDestroy {
  private meetings$$: BehaviorSubject<Meeting[]> = new BehaviorSubject<Meeting[]>([]);
  meetings$: Observable<Meeting[]> = this.meetings$$.asObservable();
  dataSource$: Observable<CalenderViewDataSource[]>;
  allMeetingTypes$: Observable<MeetingType[]>;
  personalEventTypeMeetings$: Observable<Calendar>;
  personalEventTypes$: Observable<MeetingType[]>;
  teamEventTypeMeetings$: Observable<{ [calendarId: string]: GroupMeetingType[] }>;
  teamEventTypes$: Observable<MeetingType[]> = of(null);

  // to identify new Date meeting
  previousDate = '';

  hideLoader$ = new BehaviorSubject<boolean>(false);

  eventTypeFilter$$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  meetingTimeSpanFilter$$: BehaviorSubject<TimeSpan> = new BehaviorSubject({
    start: new Date(),
    end: this.getTenYearsLater(),
  });
  updatedAttendeeCount$$: BehaviorSubject<{ meetingId: string; count: number } | null> = new BehaviorSubject(null);
  selectedTab: 'upcoming' | 'past' = 'upcoming';
  now = new Date();
  selectedEventTypeFilter: string;
  timeRangeKeySelected = '90days';

  // Pagination state
  private allMeetings: Meeting[] = [];
  hasMore = false;
  nextCursor = '';
  isLoadingMore = false;
  isInitialLoad = true;
  private ignoreNextDateRangeChange = false;
  timeRangeOptions = [
    {
      key: '7days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.7_DAYS'),
      dateRange: [moment(this.now).subtract(7, 'days').toDate(), this.now],
    },
    {
      key: '30days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.30_DAYS'),
      dateRange: [moment(this.now).subtract(30, 'days').toDate(), this.now],
    },
    {
      key: '90days',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.90_DAYS'),
      dateRange: [moment(this.now).subtract(90, 'days').toDate(), this.now],
    },
    {
      key: '6months',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.6_MONTHS'),
      dateRange: [moment(this.now).subtract(6, 'months').toDate(), this.now],
    },
    {
      key: '12months',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.12_MONTHS'),
      dateRange: [moment(this.now).subtract(12, 'months').toDate(), this.now],
    },
    {
      key: 'custom',
      name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TIME_FILTER.CUSTOM'),
      dateRange: [moment(this.now).subtract(90, 'days').toDate(), this.now],
    },
  ];
  private subscriptions: Subscription[] = [];

  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly translate: TranslateService,
    @Inject(MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$) protected context: MS_CONTEXT,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly dialog: MatDialog,
    private readonly environmentService: EnvironmentService,
    private readonly snackbarService: SnackbarService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.meetingSchedulerStoreService
      .loadHasHostEverBeenConfigured()
      .pipe(take(1))
      .subscribe((hasHostEverBeenConfigured) => {
        if (hasHostEverBeenConfigured)
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.VIEW_MY_MEETINGS,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
      });

    this.personalEventTypes$ = this.meetingSchedulerStoreService
      .loadPersonalMeetingTypes({ returnCache: false })
      .pipe(
        map((meetingTypes) =>
          meetingTypes ? meetingTypes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })) : null,
        ),
      );
    this.personalEventTypeMeetings$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.teamEventTypeMeetings$ = this.meetingSchedulerStoreService.loadGroupMeetingTypes({ returnCache: true });

    this.teamEventTypes$ = this.teamEventTypeMeetings$.pipe(map(meetingTypeMapToArray));

    this.allMeetingTypes$ = combineLatest([this.personalEventTypes$, this.teamEventTypes$]).pipe(
      map(([personalMeetings, teamMeetings]) => sortAndJoinMeetingsTypes(personalMeetings, teamMeetings)),
      shareReplay(1),
    );

    const loadMeetingFilters$ = this.setupLoadMeetingFilters();
    this.subscriptions.push(
      loadMeetingFilters$
        .pipe(
          switchMap((filters) => {
            this.allMeetings = [];
            this.nextCursor = '';
            this.hasMore = false;
            this.isInitialLoad = true;
            this.hideLoader$.next(false);
            return this.loadMeetings(filters, '');
          }),
        )
        .subscribe({
          next: (response) => {
            this.allMeetings = response.meetings;
            this.hasMore = response.hasMore;
            this.nextCursor = response.cursor;
            this.isInitialLoad = false;
            this.hideLoader$.next(true);
            this.meetings$$.next(response.meetings);
          },
          error: () => {
            this.isInitialLoad = false;
            this.hideLoader$.next(true);
            this.meetings$$.next([]);
          },
        }),
    );

    const sessionCalendarId$ = this.meetingSchedulerStoreService.loadSessionCalendar().pipe(
      map((sessionCalendar) => sessionCalendar?.id),
      startWith(null),
      shareReplay(1),
    );

    this.dataSource$ = combineLatest([this.meetings$, sessionCalendarId$, this.updatedAttendeeCount$$]).pipe(
      map(([meetings, sessionCalendarId, updatedAttendeeCount]) =>
        meetings
          .filter((m) => m.id)
          .map((m) => {
            const isSessionEvent = sessionCalendarId && m.calendarId === sessionCalendarId;
            const attendees = m.attendees || [];
            const primaryAttendee = attendees.find((a) => a.isPrimary) || { email: '', phoneNumber: '' };
            const hostDetails = m?.hostDetails || [];

            const baseItem: CalenderViewDataSource = {
              meetingId: m.id!,
              title: m.topic || '',
              eventTypeId: m.eventTypeId,
              joinMeeting: m.joinMeetingUrl.replace('https', 'http') || '',
              date: { start: m.start, end: m.end },
              guest: attendees[0] ? `${attendees[0].firstName} ${attendees[0].lastName}` : '',
              contact: primaryAttendee,
              attendeeEmails: isSessionEvent
                ? ''
                : [primaryAttendee.email, ...attendees.filter((a) => !a.isPrimary).map((a) => a.email)]
                    .filter(Boolean)
                    .join(', '),
              comments: m.description || '',
              location: m.location,
              created: m.created,
              formAnswers: m.formAnswers.formAnswers,
              attachments: m.attachments,
              calendarId: m.calendarId,
              meetingType: m.meetingType,
              hostNames: hostDetails.map((u) => u.name).filter(Boolean),
              hostUserIds: hostDetails.map((u) => (u as any).hostID).filter(Boolean),
              topic: m.topic,
              isSessionEventType: isSessionEvent,
              sessionTitle: m.sessionTitle || m.topic,
              sessionRegistrationUrl: m.sessionRegistrationUrl || '',
              maxAttendeeCount: String(m.maxAttendeeCount || '0'),
              registeredAttendeeCount: String(m.registeredAttendeeCount || 0),
              registrationCutoff: m.registrationCutoff || '',
              attendees: m.attendees,
            };

            return updatedAttendeeCount?.meetingId === m.id
              ? { ...baseItem, registeredAttendeeCount: String(Math.max(0, updatedAttendeeCount.count)) }
              : baseItem;
          }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openMeetingLink(url: string): void {
    this.analyticsService.trackEvent(POSTHOG_KEYS.MEETING_JOINED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
    const w = openSecureNewTab(url);
    w?.focus?.();
  }

  openAttendeeListDialog(meeting: CalenderViewDataSource): void {
    const dialogRef = this.dialog.open(AttendeeListComponent, {
      data: {
        meetingId: meeting.meetingId,
        meetingTitle: meeting.topic || meeting.sessionTitle,
      },
      width: '900px',
      maxHeight: '80vh',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'attendee-list-dialog',
    });

    // Listen for dialog close and update attendee count if attendees were deleted
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.confirmedAttendeesCount) {
          this.updatedAttendeeCount$$.next({ meetingId: meeting.meetingId, count: result.confirmedAttendeesCount });
        }
      });
  }

  generateEmbedCode(meeting: CalenderViewDataSource): string {
    const env = this.environmentService.getEnvironment();
    const hostUrl = env === Environment.PROD ? BOOOKING_URL_PROD : BOOOKING_URL_DEMO;
    return `<!-- bookmenow inline widget embed script begin -->
<div class="bookmenow-inline-widget" data-url="${meeting.sessionRegistrationUrl}" style="min-width:320px; height:710px;"></div>
<script type="text/javascript" src="${hostUrl}/assets/js/widget.js"></script>
<!-- bookmenow inline widget embed script end -->`;
  }

  handleCopyEmbedCodeClicked(): void {
    this.snackbarService.openSuccessSnack('MEETING_SCHEDULER.MEETING_LIST.EMBED_CODE_COPIED');
  }

  isSameDate(newDate: string, index: number): boolean {
    // Avioding multiple calls that cause previousDate have same value as newDate
    if (index === 0) {
      this.previousDate = newDate;
      return false;
    }

    if (this.previousDate === newDate) {
      return true;
    } else {
      this.previousDate = newDate;
      return false;
    }
  }

  filterByMeetingTypeIds(meetingTypeIds: string[]): void {
    this.selectedEventTypeFilter = meetingTypeIds[0];
    this.eventTypeFilter$$.next(meetingTypeIds);
  }

  stopPropagation(e: Event): void {
    // used to prevent event propagation for anchor tag
    e.stopPropagation();
  }

  trackByMeetingId(index: number, item: CalenderViewDataSource): string {
    // Include start time in tracking to detect rescheduled meetings
    return `${item.meetingId}-${item.date.start.getTime()}`;
  }

  onMeetingRescheduled(event: { id: string; newDate: Date }): void {
    // Update the meeting in the local array with the new time
    const meetingIndex = this.allMeetings.findIndex(m => m.id === event.id);
    
    if (meetingIndex !== -1) {
      const meeting = this.allMeetings[meetingIndex];
      const duration = meeting.end.getTime() - meeting.start.getTime();
      const newEnd = new Date(event.newDate.getTime() + duration);
      
      // Update the meeting with new times
      const updatedMeeting = {
        ...meeting,
        start: event.newDate,
        end: newEnd,
      };
      
      // Update in the array
      this.allMeetings[meetingIndex] = updatedMeeting;
      
      // Re-sort meetings by start time
      this.allMeetings.sort((a, b) => a.start.getTime() - b.start.getTime());
      
      // Emit updated array to trigger UI update
      this.meetings$$.next([...this.allMeetings]);
      
      // Trigger change detection
      this.cdr.markForCheck();
    } else {
      // Fallback: If meeting not found in local array, do a full reload
      this.reloadMeetings();
    }
  }


  private reloadMeetings(): void {
    // Invalidate cache first
    this.meetingSchedulerStoreService.invalidateMeetingsCache();
    
    // Reset pagination state to prevent loadMoreMeetings from using stale cursor/hasMore during reload
    this.hasMore = false;
    this.nextCursor = '';
    this.isLoadingMore = false;
    
    // Clear everything
    this.allMeetings = [];
    this.meetings$$.next([]);
    this.hideLoader$.next(false);
    
    const filters: LoadMeetingFilters = {
      meetingTypeIds: this.eventTypeFilter$$.getValue(),
      timeSpan: this.meetingTimeSpanFilter$$.getValue(),
    };

    // Force refresh to bypass shareReplay cache
    this.subscriptions.push(
      this.meetingSchedulerStoreService
        .loadPersonalMeetings({
          filters: {
            timeSpan: filters.timeSpan,
            meetingTypeIds: filters.meetingTypeIds && filters.meetingTypeIds.length > 0 ? filters.meetingTypeIds : [],
          },
          timeZone: { id: moment.tz.guess() },
          pageSize: 20,
          cursor: '',
          forceRefresh: true, // This bypasses shareReplay cache
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            // Update all properties with fresh data
            this.allMeetings = [...response.meetings];
            this.hasMore = response.hasMore;
            this.nextCursor = response.cursor;
            
            // Emit new meetings to trigger dataSource$ recalculation
            this.meetings$$.next([...response.meetings]);
            this.hideLoader$.next(true);
            
            // Trigger change detection for OnPush strategy
            this.cdr.markForCheck();
          },
          error: () => {
            this.hideLoader$.next(true);
            this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
            this.cdr.markForCheck();
          },
        })
    );
  }

  // Removed map-based updates; single dialog implies single meeting adjustment at a time

  changedMeetingListTab(value: 'upcoming' | 'past'): void {
    this.selectedTab = value;
    let timeSpan: TimeSpan;
    if (value === 'upcoming') {
      timeSpan = { start: this.now, end: this.getTenYearsLater() };
    } else {
      timeSpan = { start: this.get90DaysPrevious(), end: this.now };
      this.ignoreNextDateRangeChange = true;
    }
    this.meetingTimeSpanFilter$$.next(timeSpan);
  }

  setDateRange(range?: [Date, Date]): void {
    if (this.selectedTab === 'past' && range) {
      if (this.ignoreNextDateRangeChange) {
        this.ignoreNextDateRangeChange = false;
        return;
      }

      const start = moment(range[0]);
      let end = moment(range[1]);
      const now = moment();
      const endsToday = end.isSame(now, 'day');
      if (endsToday) {
        end.add(now.hour(), 'hour').add(now.minute(), 'minute').add(now.second(), 'second');
      } else {
        end = end.endOf('day');
      }

      const currentTimeSpan = this.meetingTimeSpanFilter$$.getValue();
      const newStartTime = start.toDate().getTime();
      const newEndTime = end.toDate().getTime();

      if (newStartTime !== currentTimeSpan.start.getTime() || newEndTime !== currentTimeSpan.end.getTime()) {
        this.meetingTimeSpanFilter$$.next({
          start: start.toDate(),
          end: end.toDate(),
        });
      }
    }
    if (this.timeRangeKeySelected === 'custom' && range) {
      this.timeRangeOptions.forEach((option) => {
        if (option.key === 'custom') {
          option.dateRange = range;
        }
      });
    }
  }

  setTimeRangeKey(key: string): void {
    this.timeRangeKeySelected = key;
    
    // Update the time span filter when time range key changes
    if (key !== 'custom') {
      const selectedOption = this.timeRangeOptions.find(option => option.key === key);
      if (selectedOption && selectedOption.dateRange) {
        const [start, end] = selectedOption.dateRange;
        this.meetingTimeSpanFilter$$.next({
          start: start,
          end: end,
        });
      }
    }
  }

  private getTenYearsLater(): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 10);
    return date;
  }

  private get90DaysPrevious(): Date {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return ninetyDaysAgo;
  }

  private setupLoadMeetingFilters(): Observable<LoadMeetingFilters> {
    const meetingTypeFilter$ = this.eventTypeFilter$$.pipe(startWith([]), distinctUntilChanged());
    const timeSpanFilter$ = this.meetingTimeSpanFilter$$.pipe(
      distinctUntilChanged(
        (a: TimeSpan, b: TimeSpan) => a.start.getTime() === b.start.getTime() && a.end.getTime() === b.end.getTime(),
      ),
    );
    return combineLatest([meetingTypeFilter$, timeSpanFilter$]).pipe(
      map(([meetingTypeIds, timeSpan]) => ({ meetingTypeIds, timeSpan })),
    );
  }

  private loadMeetings(
    filters: LoadMeetingFilters,
    cursor: string,
  ): Observable<{ meetings: Meeting[]; hasMore: boolean; cursor: string }> {
    const { meetingTypeIds, timeSpan } = filters;

    return this.meetingSchedulerStoreService
      .loadPersonalMeetings({
        filters: {
          timeSpan: timeSpan,
          meetingTypeIds: meetingTypeIds && meetingTypeIds.length > 0 ? meetingTypeIds : [],
        },
        timeZone: { id: moment.tz.guess() },
        pageSize: 20,
        cursor,
      })
      .pipe(
        catchError((error) => {
          this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
          this.hideLoader$.next(true);
          throw error;
        }),
      );
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;
    const threshold = 100;

    if (scrollHeight - scrollPosition <= threshold && !this.isLoadingMore && this.hasMore) {
      this.loadMoreMeetings();
    }
  }

  loadMoreMeetings(): void {
    if (!this.hasMore || this.isLoadingMore) return;

    this.isLoadingMore = true;
    const filters: LoadMeetingFilters = {
      meetingTypeIds: this.eventTypeFilter$$.getValue(),
      timeSpan: this.meetingTimeSpanFilter$$.getValue(),
    };

    this.subscriptions.push(
      this.loadMeetings(filters, this.nextCursor).subscribe({
        next: (response) => {
          this.allMeetings = [...this.allMeetings, ...response.meetings];
          this.hasMore = response.hasMore;
          this.nextCursor = response.cursor;
          this.isLoadingMore = false;
          this.meetings$$.next([...this.allMeetings]);
        },
        error: () => {
          this.isLoadingMore = false;
          this.snackbarService.openErrorSnack('COMMON.ERRORS.GENERIC');
        },
      }),
    );
  }
}

function meetingTypeMapToArray(meetingTypeMap: { [key: string]: MeetingType[] }): MeetingType[] {
  return Object.values(meetingTypeMap || {}).reduce((acc, types) => acc.concat(types), []);
}

function sortAndJoinMeetingsTypes(mts1: MeetingType[], mts2: MeetingType[]): MeetingType[] {
  return [...sortMeetingTypes(mts1), ...sortMeetingTypes(mts2)].map((mt) => ({
    ...mt,
    displayName: isGroupMeetingType(mt) ? `${mt.calendarName}: ${mt.name}` : mt.name,
  }));
}

function sortMeetingTypes(meetingTypes: MeetingType[]): MeetingType[] {
  return (meetingTypes || []).sort((mt1, mt2) => {
    const mt1Name = isGroupMeetingType(mt1) ? `${mt1.calendarName} ${mt1.name}` : mt1.name;
    const mt2Name = isGroupMeetingType(mt2) ? `${mt2.calendarName} ${mt2.name}` : mt2.name;
    return mt1Name.localeCompare(mt2Name, undefined, { numeric: true, sensitivity: 'base' });
  });
}
